import { useCallback, useEffect, useMemo } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { isEmpty, omit } from "lodash"
import Modal from "components/modal"
import StaticAlert from "modules/static-alert"
import SecurityProfile from "modules/security-profile"
import FactorProfile from "modules/factor-profile"
import Notifications from "modules/notifications"
import Dialog from "modules/dialog"
import { DATE } from "helpers/shapes"
import { DATA_STATUSES, OK, isLoading } from "helpers/data-statuses"
import LoadingWrapper from "components/loading-wrapper"
import ErrorPage from "components/404"
import saveRecentResources from "hocs/saveRecentResources"
import withPreferenceContext from "hocs/withPreferenceContext"
import NavRailShell from "connected-components/global-nav-rail/shell"
import { useLastRouterMatchProp } from "hooks/router"
import useOnceResolved from "hooks/once-resolved"
import { useShareMyViewSub } from "hooks/share-my-view"
import { getQueryStringState, stringifyState } from "helpers/queryString"
import { LINK, SUB_LINK, CONTROLS, PORTFOLIO } from "./shapes"
import Nav from "./components/nav"
import Subnav from "./components/subnav"

import "./style.scss"

function PortfolioRoot({
  account,
  portfolioBenchmark,
  portfolioName,
  modelId,
  modelName,
  links,
  subLinks = [],
  controls,
  security,
  factorId,
  latestPortfolios,
  closeFactorProfile,
  closeSecurityProfile,
  isActive,
  selectedBase,
  setSelectedBase,
  searchValue,
  baseInputFocus,
  baseHistory,
  searchResults,
  baseInputBlur,
  baseSearch,
  baseDropdownVisible,
  defaultSelectedBase,
  portfolioHasData,
  navigationDisabled,
  modifiedScreens,
  openDialog,
  openCompositeSecurity,
  portfolioId,
  portfolioUUID,
  noRestrictionModels,
  sessionExpired,
  portfolioDates,
  menuItems,
  modelSupportsComposites,
  unsupportedCurrencyWarning,
  dispatch,
  setModel,
  hasEnoughBaseCoverage,
  coverageNotification,
  loadActiveBase,
  updateStateFromQueryString,
  status,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  // Was implied by the view rendering at all; the shell now renders first.
  const isResolved = status === OK
  // Separate from `isResolved`: ERROR is terminal, so the chrome must stop showing a loading
  // affordance even though the data it was waiting for is never going to arrive.
  const isPending = isLoading(status)

  useEffect(() => {
    if (isResolved && !portfolioHasData) {
      navigate(`/${account}/p/${portfolioId}/upload`)
    }
  }, [navigate, isResolved, portfolioHasData, account, portfolioId])

  // Keyed on the portfolio: the shell now survives a switch, so a plain latch would run the
  // bootstrap for the first portfolio only.
  useOnceResolved(isResolved, portfolioId, () => {
    const parsedSearch = getQueryStringState()
    if (!isEmpty(parsedSearch)) {
      updateStateFromQueryString(parsedSearch, modelId, account)
    }
    // We display this message ONLY if we mount and active is already on AND there's no base specified in the URL
    // Otherwise, it'll get displayed by the active toggle or the setting of the base
    if (isActive && !parsedSearch.base) {
      loadActiveBase()
    }

    const stringifiedSearch = stringifyState(omit(parsedSearch, ["modelId", "base"]))
    if (stringifiedSearch) {
      navigate(`${location.pathname}${stringifiedSearch ? `?${stringifiedSearch}` : ""}`, { replace: true })
    }
  })

  const onSharePropsRequested = useCallback(() => {
    return { modelId, base: isActive ? selectedBase : null }
  }, [modelId, selectedBase, isActive])
  useShareMyViewSub("portfolio-root", onSharePropsRequested)

  useEffect(() => {
    if (isResolved && isActive && !hasEnoughBaseCoverage) {
      coverageNotification()
    }
  }, [coverageNotification, hasEnoughBaseCoverage, isActive, isResolved])

  const noContainer = useLastRouterMatchProp("noContainer", false)

  const outletContext = useMemo(() => ({
    modelId,
    selectedBase,
    isActive,
    portfolioDates,
    portfolioUUID,
  }), [modelId, selectedBase, isActive, portfolioDates, portfolioUUID])

  // An unresolved portfolio has no dates yet; blanking here would take the chrome with it.
  if (isResolved && !portfolioHasData) {
    return null
  }

  return <div className="modules_portfolio-root">
      <Nav
        account={account}
        modelId={modelId}
        navigationDisabled={navigationDisabled}
        links={links}
        portfolioName={portfolioName}
        isLoading={isPending}
        latestPortfolios={latestPortfolios}
        menuItems={menuItems}
      />
      <NavRailShell
        searchModelId={modelId}
        // Nothing to show until the controls resolve; the empty band holds the row's height.
        subnav={!isResolved ? <div className="subnav" /> : <Subnav
          setModel={setModel}
          subLinks={subLinks}
          portfolioBenchmark={portfolioBenchmark}
          isActiveViewType={isActive}
          controls={controls}
          setSelectedBase={setSelectedBase}
          selectedBase={selectedBase}
          modelId={modelId}
          modelName={modelName}
          searchValue={searchValue}
          baseInputFocus={baseInputFocus}
          baseDropdownVisible={baseDropdownVisible}
          baseHistory={baseHistory}
          searchResults={searchResults}
          baseInputBlur={baseInputBlur}
          baseSearch={baseSearch}
          defaultSelectedBase={defaultSelectedBase}
          modifiedScreens={modifiedScreens}
          openDialog={openDialog}
          openCompositeSecurity={openCompositeSecurity}
          portfolioId={portfolioId}
          noRestrictionModels={noRestrictionModels}
          portfolioDates={portfolioDates}
          modelSupportsComposites={modelSupportsComposites}
          unsupportedCurrencyWarning={unsupportedCurrencyWarning}
          dispatch={dispatch}
        />}
      >
        {/* `errorComponent` keeps the module's own `error: ErrorPage`, now beside the chrome rather than instead of it. */}
        <LoadingWrapper status={[status]} errorComponent={<ErrorPage />}>
          {noContainer ? <Outlet context={outletContext} />
            : <div className="main">
              <div className="container-fluid">
                <StaticAlert />
                <Outlet context={outletContext} />
              </div>
            </div>}
        </LoadingWrapper>
      </NavRailShell>
      <Notifications />
      {security && <Modal superModalOpened={sessionExpired} onClose={closeSecurityProfile} className="modules_security-profile" noButton>
        <SecurityProfile security={security} modelId={modelId} onClose={closeSecurityProfile} isPortfolioContext />
      </Modal>}
      {factorId && <Modal superModalOpened={sessionExpired} onClose={closeFactorProfile} className="modules_factor-profile" noButton>
        <FactorProfile portfolioId={portfolioId} factorId={factorId} modelId={modelId} onClose={closeFactorProfile} />
      </Modal>}
      <Dialog />
  </div>
}

PortfolioRoot.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  portfolioUUID: PropTypes.string,
  security: PropTypes.shape({
    ticker: PropTypes.string.isRequired,
    exchange: PropTypes.string.isRequired,
  }),
  factorId: PropTypes.string,
  links: PropTypes.arrayOf(LINK).isRequired,
  subLinks: PropTypes.arrayOf(SUB_LINK),
  controls: CONTROLS,
  modelId: PropTypes.string,
  modelName: PropTypes.string,
  portfolioName: PropTypes.string,
  latestPortfolios: PropTypes.arrayOf(PORTFOLIO).isRequired,
  closeFactorProfile: PropTypes.func.isRequired,
  closeSecurityProfile: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  portfolioBenchmark: PropTypes.object,
  selectedBase: PropTypes.object,
  setSelectedBase: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  baseInputFocus: PropTypes.func.isRequired,
  baseHistory: PropTypes.array.isRequired,
  searchResults: PropTypes.array.isRequired,
  baseInputBlur: PropTypes.func.isRequired,
  baseSearch: PropTypes.func.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  defaultSelectedBase: PropTypes.object,
  portfolioHasData: PropTypes.bool.isRequired,
  navigationDisabled: PropTypes.bool.isRequired,
  loadActiveBase: PropTypes.func.isRequired,
  setModel: PropTypes.func.isRequired,
  modifiedScreens: PropTypes.array.isRequired,
  openDialog: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  hasEnoughBaseCoverage: PropTypes.bool,
  coverageNotification: PropTypes.func.isRequired,
  noRestrictionModels: PropTypes.array.isRequired,
  sessionExpired: PropTypes.bool,
  portfolioDates: PropTypes.shape({
    earliest: DATE,
    latest: DATE,
  }),
  menuItems: PropTypes.array,
  status: PropTypes.oneOf(DATA_STATUSES).isRequired,
  modelSupportsComposites: PropTypes.bool.isRequired,
  unsupportedCurrencyWarning: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  updateStateFromQueryString: PropTypes.func.isRequired,
}

// [TODO] make a hook
export default saveRecentResources(withPreferenceContext(PortfolioRoot, "portfolio"), "portfolioUUID")
